import { STATE } from 'app/constants';
import { ActionDispatching, ComplexValue } from 'app/types-shared';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { isForbiddenStatus, blockAccess } from 'app/area/shared/actions-utils';
import { api as codeSelectorApi } from 'app/area/code-selector/api';
import { FilterSearchModel } from 'app/area/search-filters/types-store';
import { codetableAdministrationTranslation as text } from '../constants';
import { api, codetableOptionsActionCreators, LimitFieldsTo } from '../common';
import { api as commonApi } from '../common/api';
import { codeFieldIncludeInactive } from '../common/codetable-options-handler/constants';
import { ScreenPicker } from '../item/screen-renderer/types';
import { mapCodeItemAccess } from '../item/mapping/map-code-item';
import { mapListToStore } from './mapping/map-list-to-store';
import {
  RequestCodeItems, ReceiveCodeItems, FailCodeItems,
  ExpandCodeItem, InvalidateCodeItems,
  UpdateCategory, UpdateQuery, FailCodetableAdministrationList,
  RequestCodeItemDelete, ReceiveCodeItemDelete, FailCodeItemDelete,
  RequestCodetableAdministrationList, ReceiveCodetableAdministrationList, UpdateIncludeInactive,
  ClearCodesAccess, RequestCodesAccess, ReceiveCodesAccess, FailCodesAccess, UpdateSearchModel,
  ResetListState as ResetListState
} from './action-types';
import { CodeFieldWireKey, CodeFieldFlagWireKey, CodeFieldIncludeInactiveKey, CodetableFeature } from '../types-store';
import { defaultIncludeInactiveCodes, defaultMaxResults } from './constants';
import { LoadCodeItemsPayload } from './types-store';

export const actionCreators = {

  ...codetableOptionsActionCreators,

  updateSearchModel: (model: FilterSearchModel): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new UpdateSearchModel(model));
    },

  loadCodetableAdministrationList: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new RequestCodetableAdministrationList());

      try {
        const codetables = await codeSelectorApi.codetableAdministrationList();

        const codeItems = codetables.rootCodes ? codetables.rootCodes.map(codeItemToStoreValue) : [];

        dispatch(new ReceiveCodetableAdministrationList(codeItems));
      } catch (err) {
        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationCodetable,
            translationId: text.no_access_to_codetable_administration
          });
        } else if (err && (err.userMessages || err.message)) {
          const error = err && (err.userMessages || err.message);
          error && dispatch(new ErrorNotification(error));
        }

        dispatch(new FailCodetableAdministrationList());
      }
    },

  resetListState: (): ActionDispatching<any, any> =>
    async (dispatch, getState) => {
      dispatch(new ResetListState());
      dispatch(new UpdateIncludeInactive(CodeFieldIncludeInactiveKey.active));
    },

  loadCodeItems: (payload: LoadCodeItemsPayload):
    ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      const { codetableId, parentId, categoryId, startIndex = 0, maxResults = defaultMaxResults,
        includeInactiveCodes = codeFieldIncludeInactive[defaultIncludeInactiveCodes],
        ignoreCategoryFilters, searchModel = { query: { text: '' } }, shouldLoadCodeItems = true, limitFieldsTo } = payload;

      dispatch(new RequestCodeItems(codetableId, parentId, categoryId, searchModel));

      if (!shouldLoadCodeItems) {
        dispatch(new ReceiveCodeItems(codetableId, parentId, categoryId, searchModel, [], 0, startIndex === 0));
        return;
      }

      const defaultLimitFieldsTo: LimitFieldsTo = [
        CodeFieldWireKey.code,
        CodeFieldWireKey.category,
        CodeFieldWireKey.node,
        CodeFieldFlagWireKey.colour,
        CodeFieldFlagWireKey.icon,
        CodetableFeature.parent,
        ScreenPicker.likelihood,
        ScreenPicker.recurrence,
        ScreenPicker.severity
      ];

      try {
        const codeItemsResponse = await api.loadCodeItems({
          id: codetableId,
          parentId,
          categoryId,
          model: searchModel,
          startIndex,
          maxResults,
          displayNotSelected: true,
          limitFieldsTo: limitFieldsTo ?? defaultLimitFieldsTo,
          includeInactiveCodes,
          ignoreCategoryFilters
        });

        const codeItems = mapListToStore(codeItemsResponse.results);

        const numberOfResults = codeItemsResponse.totalNumberOfResults;
        const clearList = startIndex === 0;

        dispatch(new ReceiveCodeItems(codetableId, parentId, categoryId, searchModel, codeItems, numberOfResults, clearList));

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationCodetable,
            translationId: text.no_access_to_codetable_administration
          });
        } else if (err && (err.userMessages || err.message)) {
          const error = err && (err.userMessages || err.message);
          error && dispatch(new ErrorNotification(error));
        }

        dispatch(new FailCodeItems(codetableId, parentId, categoryId, searchModel));
      }
    },

  deleteCodeItem: (codetableId: number, codeId: number, successMessage: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new RequestCodeItemDelete(codetableId, codeId));
      try {
        await commonApi.deleteCodeItem(codetableId, codeId);

        dispatch(new ReceiveCodeItemDelete(codeId));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailCodeItemDelete(codeId));
      }
    },

  clearCodesAccess: (): ActionDispatching<any, any> =>
    async (dispatch, getState) => {

      dispatch(new ClearCodesAccess());
    },

  loadCodesAccess: (codetableId: number, codeId: number): ActionDispatching<any, any> =>
    async (dispatch, getState) => {

      dispatch(new RequestCodesAccess(codetableId, codeId));

      try {
        const result = await api.loadCodesAccess(codetableId, codeId);
        const mappedCodesAccess = mapCodeItemAccess(codeId, result);

        dispatch(new ReceiveCodesAccess(mappedCodesAccess));

      } catch (err) {
        dispatch(new FailCodesAccess(codetableId, codeId));
      }
    },

  expandCodeItem: (parentId: number): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new ExpandCodeItem(parentId));
    },

  invalidateCodeItems: (): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new InvalidateCodeItems());
    },

  updateCaseCategory: (value: ComplexValue<number>): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new UpdateCategory(value));
    },

  updateQuery: (value: string): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new UpdateQuery(value));
    },

  updateIncludeInactive: (value: CodeFieldIncludeInactiveKey): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new UpdateIncludeInactive(value));
    }
};
